import { useEffect, useRef, useState } from 'react';
import { useAppStore } from '@/lib/store';
import { processPhotoSmart, initMLModels } from '@/lib/ml-worker-api';
import { calibrateFromHeight } from '@/lib/reference-calibration';
import { extractMeasurements } from '@/lib/measurement-engine';
import { processKeyframes, convertObservationsToCm } from '@/lib/rotation-processor';
import type { BodyMeasurements } from '@/types/measurements';

interface ProgressStep {
  label: string;
  message: string;
}

const PROGRESS_STEPS: ProgressStep[] = [
  { label: 'Loading', message: 'Loading AI models...' },
  { label: 'Detecting', message: 'Detecting your body pose...' },
  { label: 'Analyzing', message: 'Analyzing body shape...' },
  { label: 'Measuring', message: 'Calculating measurements...' },
  { label: 'Done', message: 'All done!' },
];

export function ProcessingScreen() {
  const {
    currentStep,
    frontPhoto,
    sidePhoto,
    frontDetection: storedFrontDetection,
    gender,
    wornClothing,
    userHeightCm,
    rotationKeyframes,
    setFrontDetection,
    setSideDetection,
    setMeasurements,
    setStep,
    setError,
  } = useAppStore();

  const isSideProcessing = currentStep === 'processing_side';
  const isRotationProcessing = currentStep === 'processing_rotation';

  const [stepIndex, setStepIndex] = useState(0);
  const [error, setLocalError] = useState<string | null>(null);
  const [failedStep, setFailedStep] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    processPhoto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function processPhoto() {
    // Rotation processing path
    if (isRotationProcessing) {
      await processRotationKeyframes();
      return;
    }

    const photoToProcess = isSideProcessing ? sidePhoto : frontPhoto;

    if (!photoToProcess || !wornClothing || !userHeightCm) {
      setLocalError('Missing required data. Please restart.');
      setFailedStep('setup');
      return;
    }

    // Clear previous errors on retry
    setLocalError(null);
    setFailedStep(null);

    try {
      // Step 1: Load models (downloads ~13MB on first use, cached afterward)
      setStepIndex(0);
      try {
        await initMLModels();
      } catch (initErr) {
        const detail = initErr instanceof Error ? initErr.message : String(initErr);
        let userMessage: string;
        if (detail.includes('timed out')) {
          userMessage = 'The AI model download timed out. Try a stronger Wi-Fi connection.';
        } else if (detail.includes('Failed to fetch') || detail.includes('NetworkError')) {
          userMessage = 'Could not download AI models. Please check your internet connection.';
        } else {
          userMessage = `Could not load AI models. Try "Clear Cache & Reload" below.`;
        }
        setLocalError(userMessage);
        setFailedStep('Loading AI models');
        console.error('ML init failed:', detail);
        return;
      }

      // Step 2: Create image element and detect
      setStepIndex(1);
      const img = await loadImage(photoToProcess);

      // Step 3: Run ML processing
      setStepIndex(2);
      const detection = await processPhotoSmart(img);

      if (!detection.landmarks || detection.landmarks.length === 0) {
        if (isSideProcessing && detection.segmentationMask) {
          // Side photo: landmarks optional, segmentation mask is sufficient
          // for measuring torso depth. Proceed with empty landmarks.
        } else {
          setLocalError(
            isSideProcessing
              ? 'Could not detect your body in the side photo. Please retake with your full body visible.'
              : 'Could not detect your body in the photo. Please retake with your full body visible.'
          );
          setFailedStep('Detecting body pose');
          return;
        }
      }

      // Step 4: Calibrate and calculate measurements
      setStepIndex(3);

      if (isSideProcessing) {
        // Side photo processing: store detection, recalculate with side data
        setSideDetection(detection);

        // Re-run measurements with side data if front detection exists
        if (storedFrontDetection) {
          const calibration = calibrateFromHeight(
            storedFrontDetection.landmarks,
            userHeightCm,
            storedFrontDetection.imageHeight
          );

          const result = extractMeasurements({
            landmarks: storedFrontDetection.landmarks,
            segmentationMask: storedFrontDetection.segmentationMask,
            calibration,
            gender,
            wornClothing,
            userHeightCm,
            imageWidth: storedFrontDetection.imageWidth,
            imageHeight: storedFrontDetection.imageHeight,
            sideLandmarks: detection.landmarks,
            sideSegmentationMask: detection.segmentationMask,
            sideImageWidth: detection.imageWidth,
            sideImageHeight: detection.imageHeight,
          });

          const fullMeasurements = fillMeasurements(result.measurements);
          setMeasurements(fullMeasurements, result.overallConfidence);
        }
      } else {
        // Front photo processing
        setFrontDetection(detection);

        const calibration = calibrateFromHeight(
          detection.landmarks,
          userHeightCm,
          detection.imageHeight
        );

        // If side photo already captured (from capture_session), process it too
        let sideDetectionResult = null;
        if (sidePhoto) {
          try {
            const sideImg = await loadImage(sidePhoto);
            sideDetectionResult = await processPhotoSmart(sideImg);
            setSideDetection(sideDetectionResult);
          } catch {
            // Side processing failed — proceed with front only
          }
        }

        const measurementInput: Parameters<typeof extractMeasurements>[0] = {
          landmarks: detection.landmarks,
          segmentationMask: detection.segmentationMask,
          calibration,
          gender,
          wornClothing,
          userHeightCm,
          imageWidth: detection.imageWidth,
          imageHeight: detection.imageHeight,
        };

        if (sideDetectionResult) {
          measurementInput.sideLandmarks = sideDetectionResult.landmarks;
          measurementInput.sideSegmentationMask = sideDetectionResult.segmentationMask;
          measurementInput.sideImageWidth = sideDetectionResult.imageWidth;
          measurementInput.sideImageHeight = sideDetectionResult.imageHeight;
        }

        const result = extractMeasurements(measurementInput);

        const fullMeasurements = fillMeasurements(result.measurements);
        setMeasurements(fullMeasurements, result.overallConfidence);
      }

      // Step 5: Done
      setStepIndex(4);
      await new Promise((r) => setTimeout(r, 500));

      if (isSideProcessing) {
        setStep('results');
      } else if (sidePhoto) {
        // Side already processed in this pass (from capture_session flow)
        setStep('results');
      } else {
        // Old flow: offer optional side photo
        setStep('side_prompt');
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'An unexpected error occurred';
      setLocalError(message);
      setFailedStep(PROGRESS_STEPS[stepIndex]?.label ?? 'Processing');
      setError(message);
    }
  }

  async function processRotationKeyframes() {
    if (!storedFrontDetection || !wornClothing || !userHeightCm) {
      setLocalError('Missing front photo data. Please restart.');
      setFailedStep('setup');
      return;
    }

    if (rotationKeyframes.length === 0) {
      setLocalError('No rotation keyframes captured. Please try again.');
      setFailedStep('setup');
      return;
    }

    setLocalError(null);
    setFailedStep(null);

    try {
      // Step 1: Load ML models
      setStepIndex(0);
      try {
        await initMLModels();
      } catch (initErr) {
        const detail = initErr instanceof Error ? initErr.message : String(initErr);
        setLocalError(`Could not load AI models: ${detail}`);
        setFailedStep('Loading AI models');
        return;
      }

      // Step 2: Process keyframes (run segmentation on each)
      setStepIndex(1);
      const landmarks = storedFrontDetection.landmarks;
      const lShoulder = landmarks[11]!;
      const rShoulder = landmarks[12]!;
      const lHip = landmarks[23]!;
      const rHip = landmarks[24]!;

      const shoulderMidY = (lShoulder.y + rShoulder.y) / 2;
      const hipMidY = (lHip.y + rHip.y) / 2;
      const torsoSpan = hipMidY - shoulderMidY;

      const chestY = shoulderMidY + torsoSpan * 0.3;
      const waistY = shoulderMidY + torsoSpan * 0.6;
      const hipY = hipMidY;

      setStepIndex(2);
      const { chestObs, waistObs, hipObs } = await processKeyframes(
        rotationKeyframes,
        chestY,
        waistY,
        hipY,
        (progress) => {
          // Could update a more granular progress bar here
          if (progress > 0.5) setStepIndex(2);
        }
      );

      // Step 3: Convert px observations to cm
      setStepIndex(3);
      const calibration = calibrateFromHeight(
        landmarks,
        userHeightCm,
        storedFrontDetection.imageHeight
      );
      const pxPerCm = calibration.pixelsPerCm;

      const chestObsCm = convertObservationsToCm(chestObs, pxPerCm);
      const waistObsCm = convertObservationsToCm(waistObs, pxPerCm);
      const hipObsCm = convertObservationsToCm(hipObs, pxPerCm);

      // Step 4: Run full measurement extraction with rotation observations
      const result = extractMeasurements({
        landmarks,
        segmentationMask: storedFrontDetection.segmentationMask,
        calibration,
        gender,
        wornClothing,
        userHeightCm,
        imageWidth: storedFrontDetection.imageWidth,
        imageHeight: storedFrontDetection.imageHeight,
        rotationObservations: {
          chest: chestObsCm,
          waist: waistObsCm,
          hip: hipObsCm,
        },
      });

      const fullMeasurements = fillMeasurements(result.measurements);
      setMeasurements(fullMeasurements, result.overallConfidence);

      // Step 5: Done
      setStepIndex(4);
      await new Promise((r) => setTimeout(r, 500));
      setStep('results');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'An unexpected error occurred';
      setLocalError(message);
      setFailedStep(PROGRESS_STEPS[stepIndex]?.label ?? 'Processing');
      setError(message);
    }
  }

  function handleRetry() {
    setRetrying(true);
    setLocalError(null);
    setFailedStep(null);
    setStepIndex(0);
    processPhoto().finally(() => setRetrying(false));
  }

  async function handleClearCacheAndRetry() {
    setClearingCache(true);
    try {
      // Unregister all service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((r) => r.unregister()));
      }
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }
    } catch {
      // Ignore cache clearing errors
    }
    setClearingCache(false);
    // Reload the page to get fresh assets
    window.location.reload();
  }

  const currentProgressStep = PROGRESS_STEPS[stepIndex] ?? PROGRESS_STEPS[0]!;
  const percent = ((stepIndex + 1) / PROGRESS_STEPS.length) * 100;

  if (error) {
    return (
      <div className="screen items-center justify-center text-center">
        <div className="rounded-2xl bg-red-50 p-6">
          <p className="text-lg font-semibold text-red-700">
            Something went wrong
          </p>
          <p className="mt-2 text-sm text-red-600">{error}</p>
          {failedStep && (
            <p className="mt-1 text-xs text-red-400">
              Failed during: {failedStep}
            </p>
          )}
        </div>
        <button
          onClick={handleRetry}
          disabled={retrying}
          className="btn-primary mt-6"
        >
          {retrying ? 'Retrying...' : 'Try Again'}
        </button>
        <button
          onClick={handleClearCacheAndRetry}
          disabled={clearingCache}
          className="mt-3 w-full rounded-xl border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700"
        >
          {clearingCache ? 'Clearing...' : 'Clear Cache & Reload'}
        </button>
        <button
          onClick={() => setStep('capture_front')}
          className="btn-secondary mt-3"
        >
          Retake Photo
        </button>
        <button
          onClick={() => setStep('welcome')}
          className="mt-3 text-sm text-gray-500 underline"
        >
          Start Over
        </button>
      </div>
    );
  }

  return (
    <div className="screen items-center justify-center text-center">
      {/* Animated spinner */}
      <div className="mb-8">
        <div className="mx-auto h-20 w-20 animate-pulse-slow rounded-full border-4 border-brand-200 bg-brand-50 p-4">
          <div className="h-full w-full rounded-full bg-brand-500" />
        </div>
      </div>

      <h2 className="text-2xl font-bold text-gray-900">
        {isRotationProcessing
          ? 'Processing rotation scan'
          : isSideProcessing
            ? 'Analyzing side photo'
            : 'Analyzing your photo'}
      </h2>
      <p className="mt-2 text-sm text-gray-500">{currentProgressStep.message}</p>

      {/* Progress bar */}
      <div className="mt-8 w-full max-w-xs">
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
          <div
            className="h-full rounded-full bg-brand-500 transition-all duration-500 ease-out"
            style={{ width: `${percent}%` }}
          />
        </div>
        <div className="mt-2 flex justify-between text-xs text-gray-400">
          {PROGRESS_STEPS.map((s, i) => (
            <span
              key={s.label}
              className={i <= stepIndex ? 'text-brand-600 font-medium' : ''}
            >
              {s.label}
            </span>
          ))}
        </div>
      </div>

      <p className="mt-8 text-xs text-gray-400">
        Your photo is processed on-device. Nothing is uploaded.
      </p>
    </div>
  );
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = dataUrl;
  });
}

/** Fill a partial BodyMeasurements with zero-value placeholders for missing keys */
function fillMeasurements(
  partial: Partial<BodyMeasurements>
): BodyMeasurements {
  const placeholder = {
    valueCm: 0,
    confidence: 0,
    confidenceLevel: 'low' as const,
    method: 'proportional_estimate' as const,
  };

  return {
    height: partial.height ?? placeholder,
    neckCircumference: partial.neckCircumference ?? placeholder,
    shoulderWidth: partial.shoulderWidth ?? placeholder,
    chestCircumference: partial.chestCircumference ?? placeholder,
    waistCircumference: partial.waistCircumference ?? placeholder,
    hipCircumference: partial.hipCircumference ?? placeholder,
    armLength: partial.armLength ?? placeholder,
    upperArmCircumference: partial.upperArmCircumference ?? placeholder,
    wristCircumference: partial.wristCircumference ?? placeholder,
    inseamLength: partial.inseamLength ?? placeholder,
    outseamLength: partial.outseamLength ?? placeholder,
    thighCircumference: partial.thighCircumference ?? placeholder,
    backLength: partial.backLength ?? placeholder,
    frontLength: partial.frontLength ?? placeholder,
    torsoLength: partial.torsoLength ?? placeholder,
    acrossBackWidth: partial.acrossBackWidth ?? placeholder,
    acrossChestWidth: partial.acrossChestWidth ?? placeholder,
    kneeCircumference: partial.kneeCircumference ?? placeholder,
    calfCircumference: partial.calfCircumference ?? placeholder,
    ankleCircumference: partial.ankleCircumference ?? placeholder,
    trouserLength: partial.trouserLength ?? placeholder,
    rise: partial.rise ?? placeholder,
    sleeveLength34: partial.sleeveLength34 ?? placeholder,
    agbadaLength: partial.agbadaLength ?? placeholder,
    headCircumference: partial.headCircumference ?? placeholder,
    bustPoint: partial.bustPoint ?? placeholder,
    underBustDrop: partial.underBustDrop ?? placeholder,
    underBustCircumference: partial.underBustCircumference ?? placeholder,
    kneeLength: partial.kneeLength ?? placeholder,
    dressLength: partial.dressLength ?? placeholder,
  };
}
